import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TourProvider } from "@/context/TourContext";
import UpdateToast from "@/components/UpdateToast";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import PlanViewer from "@/pages/PlanViewer";
import Search from "@/pages/Search";
import ProjectTool from "@/pages/ProjectTool";
import Overview from "@/pages/Overview";
import Stammdaten from "@/pages/Stammdaten";
import UserManagement from "@/pages/UserManagement";
import PlatformAdmin from "@/pages/PlatformAdmin";
import Inspections from "@/pages/Inspections";
import DeletionRequests from "@/pages/DeletionRequests";
import MyTasks from "@/pages/MyTasks";
import Help from "@/pages/Help";
import AccessStatus from "@/pages/AccessStatus";
import AccessDecision from "@/pages/AccessDecision";
import AccessRequests from "@/pages/AccessRequests";
import ProblemReports from "@/pages/ProblemReports";
import AboVerwaltung from "@/pages/AboVerwaltung";
import Register from "@/pages/Register";
import AdminIndividuell from "@/pages/AdminIndividuell";
import AdminAbos from "@/pages/AdminAbos";
import Layout from "@/components/Layout";
import Legal from "@/pages/Legal";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Protected>{children}</Protected>;
}

function PlatformRoute({ children }) {
  const { user } = useAuth();
  if (user && !user.is_platform_admin) return <Navigate to="/dashboard" replace />;
  return <Protected>{children}</Protected>;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/impressum" element={<Legal page="impressum" />} />
      <Route path="/datenschutz" element={<Legal page="datenschutz" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/registrieren" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/zugang/status" element={<AccessStatus />} />
      <Route path="/zugang/entscheiden" element={<AccessDecision />} />
      <Route path="/admin/anfragen" element={<PlatformRoute><AccessRequests /></PlatformRoute>} />
      <Route path="/admin/meldungen" element={<PlatformRoute><ProblemReports /></PlatformRoute>} />
      <Route path="/admin/individuell" element={<PlatformRoute><AdminIndividuell /></PlatformRoute>} />
      <Route path="/admin/abos" element={<PlatformRoute><AdminAbos /></PlatformRoute>} />
      <Route path="/einstellungen/abo" element={<AdminRoute><AboVerwaltung /></AdminRoute>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/projects" element={<Protected><Projects /></Protected>} />
      <Route path="/projects/:projectId" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/plans/:planId" element={<Protected><PlanViewer /></Protected>} />
      <Route path="/search" element={<Protected><Search /></Protected>} />
      <Route path="/quantities" element={<Protected><ProjectTool tool="quantities" /></Protected>} />
      <Route path="/pruefungen" element={<Protected><Inspections /></Protected>} />
      <Route path="/aufgaben" element={<Protected><MyTasks /></Protected>} />
      <Route path="/hilfe" element={<Protected><Help /></Protected>} />
      <Route path="/loeschanfragen" element={<Protected><DeletionRequests /></Protected>} />
      <Route path="/daily-reports" element={<Protected><ProjectTool tool="daily-reports" /></Protected>} />
      <Route path="/overview/plans" element={<Protected><Overview view="plans" /></Protected>} />
      <Route path="/overview/tickets" element={<Protected><Overview view="tickets" /></Protected>} />
      <Route path="/overview/defects" element={<Protected><Overview view="defects" /></Protected>} />
      <Route path="/overview/photos" element={<Protected><Overview view="photos" /></Protected>} />
      <Route path="/overview/reports" element={<Protected><Overview view="reports" /></Protected>} />
      <Route path="/overview/documents" element={<Protected><Overview view="documents" /></Protected>} />
      <Route path="/stammdaten" element={<AdminRoute><Stammdaten /></AdminRoute>} />
      <Route path="/benutzer" element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path="/plattform" element={<PlatformRoute><PlatformAdmin /></PlatformRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TourProvider>
            <AppRoutes />
          </TourProvider>
          <Toaster position="top-right" richColors />
          <UpdateToast />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
